import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useCallback, useEffect, useMemo} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Expression, ExpressionId} from '../../../editors/expressions/expressionModel';
import {useExpressionsContext} from '../../../editors/expressions/expressionsContext';
import {RuleDefaultCardTypesEnum} from '../../ruleActionConditionHelpers';
import {buildConditionTypeFromExpression} from '../../ruleConditionHelpers';
import {CompareExpressionCondition, RuleCondition} from '../../ruleConditionModel';
import {RuleConditionDefinition} from '../../ruleParameterModel';
import {useOnSelectCreateExpression} from '../hooks/useOnSelectCreateExpression';
import {ExpressionParameter} from '../parameters/expressionParameter';
import {RuleConditionIcon} from '../ruleConditionIcon';
import {
  MultilineRuleParameterCardDefaultTitleStyled,
  RuleMultilineParameterCard,
} from '../ruleMultilineParameterCard';
import {RuleParameterPicker} from '../ruleParameterPicker';

/*
 * Types.
 */

interface RuleExpressionConditionCardProps {
  condition: CompareExpressionCondition;
  conditionDefinition: RuleConditionDefinition | undefined;
  errorMessage?: ReactNode;
  isDisabled?: boolean;
  onUpdate: HandlerOf<RuleCondition>;
  onDelete?: HandlerOf<RuleCondition>;
}

/*
 * Component.
 */

export const RuleExpressionConditionCard: FC<RuleExpressionConditionCardProps> = (props) => {
  const {condition, conditionDefinition, errorMessage, onUpdate, onDelete, isDisabled} = props;
  const {expressions} = useExpressionsContext();

  const parameters = conditionDefinition?.parameters ?? {};

  useEffect(() => {
    const expressionId = condition.params.expression;
    const firstExpression = expressions[0];

    // Auto-select first expression.
    if (!expressionId && firstExpression) {
      onUpdate({
        ...condition,
        type: buildConditionTypeFromExpression(firstExpression),
        params: {
          expression: firstExpression.id,
          comparator: 'is_defined',
        },
      });
      return;
    }

    const expression = expressions.find((e) => e.id === expressionId);
    if (!expression) {
      return;
    }

    const conditionType = buildConditionTypeFromExpression(expression);

    // Reset when expression result changes.
    if (condition.type !== conditionType) {
      onUpdate({
        ...condition,
        type: conditionType,
        params: {
          expression: expression.id,
          comparator: 'is_defined',
        },
      });
    }
  }, [condition, expressions, onUpdate]);

  const onExpressionChange = useCallback(
    (expression: Expression) =>
      onUpdate({
        ...condition,
        type: buildConditionTypeFromExpression(expression),
        params: {
          expression: expression.id,
          comparator: 'is_defined',
        },
      }),
    [onUpdate, condition],
  );

  const onExpressionIdChange = useCallback(
    (expressionId: ExpressionId) => {
      const expression = expressions.find((e) => e.id === expressionId);
      if (expression) {
        onExpressionChange(expression);
      }
    },
    [onExpressionChange, expressions],
  );

  const onSelectCreateExpression = useOnSelectCreateExpression(onExpressionChange);

  const onCancel = onDelete && (() => onDelete(condition));

  const extraParameters = useMemo(
    () =>
      Object.entries(parameters)
        .slice(2) // Skip expression and comparator.
        .map(([parameterName, parameter]) => (
          <RuleParameterPicker
            key={parameterName}
            parameter={parameter}
            resolvedParameter={conditionDefinition?.resolvedParameters[parameterName]}
            parameterName={parameterName}
            conditionOrAction={condition}
            cardType={RuleDefaultCardTypesEnum.CONDITION}
            onUpdate={onUpdate}
            isDisabled={isDisabled}
          />
        )),
    [parameters, conditionDefinition?.resolvedParameters, condition, onUpdate, isDisabled],
  );

  // This should not happen, but safety check.
  const errorLabel = <Trans id="definition-condition-not-found">Condition {condition.type} not found</Trans>;
  if (!conditionDefinition) {
    return <RuleMultilineParameterCard icon={<RuleConditionIcon id={condition.type} />} title={errorLabel} />;
  }

  const title = (
    <>
      <MultilineRuleParameterCardDefaultTitleStyled>
        <Trans id="rule-expression-card-variable">Variable</Trans>
      </MultilineRuleParameterCardDefaultTitleStyled>
      <ExpressionParameter
        selectedItem={condition.params.expression}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={onExpressionIdChange}
        onSelectCreateExpression={onSelectCreateExpression}
      />
      &nbsp;
      <RuleParameterPicker
        cardType={RuleDefaultCardTypesEnum.CONDITION}
        parameter={parameters.comparator}
        resolvedParameter={conditionDefinition?.resolvedParameters.comparator}
        parameterName="comparator"
        conditionOrAction={condition}
        onUpdate={onUpdate}
        isDisabled={isDisabled}
      />
    </>
  );

  return (
    <RuleMultilineParameterCard
      title={title}
      icon={<RuleConditionIcon id={condition.type} />}
      errorMessage={errorMessage}
      onCancel={onCancel}
    >
      {needsExtractParameters(condition.params.comparator) && extraParameters}
    </RuleMultilineParameterCard>
  );
};

/*
 * Helpers.
 */

// These comparators do not use the 3rd param.
function needsExtractParameters(comparator: string) {
  return comparator !== 'is_defined' && comparator !== 'is_undefined';
}
